import React from 'react';
import { FormattedDate } from 'react-intl';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { Box } from '@daedalus/atlas/helpers/Box';
import { Icon } from '@daedalus/atlas/Icon';
import { Text } from '@daedalus/atlas/Text';
import { cssTheme } from '@daedalus/atlas/themes';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { RefundType } from '@daedalus/core/src/offer/business/cancellationPenalties';
import { getDatePart } from '@daedalus/core/src/utils/date';
interface CancellationLabelPropsType {
  /** Datetime of the cancellation deadline */
  freeCancellationDeadline?: string;

  /** Datetime of the last cancellation deadline before the booking becomes non refundable but after it's no longer fully refundable. */
  partialRefundDeadline?: string;

  /** Defines in which part of app we will use label. Changes text styles, date appearance and colors */
  variant?: 'roomSummary' | 'cancellationPopUp' | 'roomDetails';

  /** Whether the cancellation deadline should be hidden */
  hideFreeCancellationDeadlineDate?: boolean;

  /** Type of cancellation penalty (e.g., non-refundable, partial, free cancellation) */
  refundType: RefundType;

  /** True if an unpaid charge later booking is refundable */
  isUnpaidChargeLaterBooking?: boolean;
}
const _exp = /*#__PURE__*/() => ({
  color
}) => color;
const MessageWrapper = /*#__PURE__*/styled('span')({
  name: "MessageWrapper",
  class: "m1wwlq2t",
  propsAsIs: false,
  vars: {
    "m1wwlq2t-0": [_exp()]
  }
});
const OuterWrapper = /*#__PURE__*/styled('div')({
  name: "OuterWrapper",
  class: "o1af34xm",
  propsAsIs: false
});
const IconWrapper = /*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "igdvl6r",
  propsAsIs: false
});
const getCancellationIcon = (refundType: RefundType, isRoomSummaryVariant?: boolean, isRoomDetailsVariant?: boolean, isUnpaidChargeLaterBooking?: boolean) => {
  const roomSummaryRefundableIconColor = isRoomSummaryVariant ? 'content.success.c600' : 'content.success.c500';
  const refundableIconColor = isRoomDetailsVariant ? 'content.neutral.c600' : roomSummaryRefundableIconColor;
  const roomSummaryNonRefundableIconColor = isRoomSummaryVariant ? 'content.neutral.c600' : 'content.danger.c700';
  const nonRefundableIconColor = isRoomDetailsVariant ? 'content.neutral.c600' : roomSummaryNonRefundableIconColor;
  const roomSummaryPartialIconColor = isRoomSummaryVariant ? 'content.neutral.c600' : 'content.warning.c600';
  const partialIconColor = isRoomDetailsVariant ? 'content.neutral.c600' : roomSummaryPartialIconColor;
  if (refundType === RefundType.freeCancellation || isUnpaidChargeLaterBooking) {
    return <Icon size={isRoomDetailsVariant ? 'sm' : 'md'} name="CalendarCheck4" colorPath={refundableIconColor} />;
  }
  if (refundType === RefundType.nonRefundable) {
    return <Icon size={isRoomDetailsVariant ? 'sm' : 'md'} name="CalendarCross3" colorPath={nonRefundableIconColor} />;
  }
  if (refundType === RefundType.partialRefund) {
    return <Icon size={isRoomDetailsVariant ? 'sm' : 'md'} name="CalendarMinus" colorPath={partialIconColor} />;
  }
  return null;
};
const getCancellationMessage = (refundType: RefundType, freeCancellationDeadline = '', partialRefundDeadline: string, isRoomSummaryVariant?: boolean, isRoomDetailsVariant?: boolean, hideFreeCancellationDeadline?: boolean, isUnpaidChargeLaterBooking?: boolean) => {
  const roomSummaryRefundableTextColor = isRoomSummaryVariant ? cssTheme.colors.content.success.c600 : cssTheme.colors.content.success.c500;
  const refundableTextColor = isRoomDetailsVariant ? cssTheme.colors.content.neutral.c950 : roomSummaryRefundableTextColor;
  const roomSummaryNonRefunableTextColor = isRoomSummaryVariant ? cssTheme.colors.content.neutral.c600 : cssTheme.colors.content.danger.c700;
  const nonRefundableTextColor = isRoomDetailsVariant ? cssTheme.colors.content.neutral.c950 : roomSummaryNonRefunableTextColor;
  const roomSummaryPartialTextColor = isRoomSummaryVariant ? cssTheme.colors.content.neutral.c600 : cssTheme.colors.content.warning.c600;
  const partialTextColor = isRoomDetailsVariant ? cssTheme.colors.content.neutral.c950 : roomSummaryPartialTextColor;
  if (refundType === RefundType.freeCancellation || isUnpaidChargeLaterBooking) {
    return freeCancellationDeadline ? <MessageWrapper color={refundableTextColor} data-id="cancellationType.refundable">
        <FormattedMessageWrapper id="refundable" defaultMessage="Free cancellation" />
        {!hideFreeCancellationDeadline && <>
            <span> </span>
            <FormattedMessageWrapper id="beforeDeadline" defaultMessage="before {date}" values={{
          date: <FormattedDate value={getDatePart(freeCancellationDeadline)} day="2-digit" month="short" timeZone="UTC" />
        }} />
          </>}
      </MessageWrapper> : <MessageWrapper color={refundableTextColor} data-id="cancellationType.freeCancellation">
        <FormattedMessageWrapper id="refundable" defaultMessage="Free cancellation" />
      </MessageWrapper>;
  }
  if (refundType === RefundType.nonRefundable) {
    return <MessageWrapper color={nonRefundableTextColor} data-id="cancellationType.nonRefundable">
        <FormattedMessageWrapper id="cancellationPenalties.nonRefundable" defaultMessage="Non-refundable" />
      </MessageWrapper>;
  }
  if (refundType === RefundType.partialRefund) {
    return partialRefundDeadline ? <MessageWrapper color={partialTextColor} data-id="cancellationType.partial">
        <FormattedMessageWrapper id="partialRefund" defaultMessage="Partial refund" />
        <span> </span>
        <FormattedMessageWrapper id="beforeDeadline" defaultMessage="before {date}" values={{
        date: <FormattedDate value={getDatePart(partialRefundDeadline)} day="2-digit" month="short" timeZone="UTC" year="numeric" />
      }} />
      </MessageWrapper> : <MessageWrapper color={isRoomDetailsVariant ? cssTheme.colors.content.neutral.c950 : cssTheme.colors.content.neutral.c600} data-id="cancellationType.partiallyRefundable">
        <FormattedMessageWrapper id="cancellationPenalties.partiallyRefundable" defaultMessage="Partially refundable" />
      </MessageWrapper>;
  }
  return null;
};
export const CancellationLabel = ({
  freeCancellationDeadline,
  partialRefundDeadline = '',
  variant = 'roomSummary',
  hideFreeCancellationDeadlineDate,
  refundType,
  isUnpaidChargeLaterBooking = false
}: CancellationLabelPropsType) => {
  const isRoomSummaryVariant = variant === 'roomSummary';
  const isRoomDetailsVariant = variant === 'roomDetails';
  const roomSummaryTextSize = isRoomSummaryVariant ? 'bodyS' : 'titleM';
  const messageTextSize = isRoomDetailsVariant ? 'bodyS' : roomSummaryTextSize;
  return <Box data-id="cancellation-label">
      <OuterWrapper>
        <IconWrapper className={cx(`--${messageTextSize}`)}>
          {getCancellationIcon(refundType, isRoomSummaryVariant, isRoomDetailsVariant, isUnpaidChargeLaterBooking)}
        </IconWrapper>
        <Box>
          <Text variant={messageTextSize}>
            {getCancellationMessage(refundType, freeCancellationDeadline, partialRefundDeadline, isRoomSummaryVariant, isRoomDetailsVariant, hideFreeCancellationDeadlineDate, isUnpaidChargeLaterBooking)}
          </Text>
        </Box>
      </OuterWrapper>
    </Box>;
};

require("./index.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");